import React from "react";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { cva, VariantProps } from "class-variance-authority";

import { Button } from "components/Button/Button";
import { FaIcon } from "components/FaIcon/FaIcon";

import "./Modal.scss";

export type ModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    className?: string;
};

const modal = cva("modal", {
    variants: {
        fullHeight: {
            true: "full-height",
        },
        size: {
            sm: "size-sm",
            md: "size-md",
            lg: "size-lg",
        },
        padded: {
            true: "padded",
        },
    },
});

export function Modal({
    children,
    open,
    setOpen,
    className,
    size = "md",
    fullHeight = false,
    padded = false,
}: React.PropsWithChildren<ModalProps> & VariantProps<typeof modal>) {
    const dialogRef = React.useRef<HTMLDialogElement>(null);

    function lockBodyScroll() {
        document.body.style.overflow = "hidden";
    }

    function unlockBodyScroll() {
        document.body.style.overflow = "";
    }

    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    // We do this because we need to open with showModal to give it modal
    // properties
    React.useEffect(() => {
        const dialogElement = dialogRef.current;

        if (dialogElement) {
            if (open) {
                lockBodyScroll();
                dialogElement.showModal();
            } else {
                unlockBodyScroll();
                dialogElement.close();
            }
        }
    }, [open, handleClose]);

    React.useEffect(() => {
        const dialogElement = dialogRef.current;

        const handleClose = () => {
            setOpen(false);
        };

        dialogElement?.addEventListener("close", handleClose);

        return () => {
            dialogElement?.removeEventListener("close", handleClose);
        };
    }, [setOpen]);

    const classes = modal({ className, fullHeight, size, padded });

    return (
        <dialog ref={dialogRef} className={classes}>
            <div className="content-container">
                {children}
                <Button
                    size="sm"
                    variation="secondary"
                    className="close-button"
                    aria-label="Close modal"
                    onClick={() => setOpen(false)}
                >
                    <FaIcon size="1x" icon={faXmark} color="high-contrast" />
                </Button>
            </div>
        </dialog>
    );
}
